"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, Eye, FileText, Calendar, MapPin, ChevronLeft, ChevronRight, Filter, X, Pencil } from "lucide-react";
import { DocumentViewPopup } from "@/components/document-view-popup";
import { GDFormPopup } from "@/components/gd-form-popup";
import { BondOrderPopup } from "@/components/bond-order-popup";
import { BNSL129NoticePopup } from "@/components/bnsl129-notice-popup";
import { CrpcBondOrderPopup } from "@/components/crpc-bond-order-popup";
import type { Document } from "@/lib/interfaces";
import apiService from "@/services/apiService";
import { getUserInfo } from "@/services/userService";
import { useAppStore } from "@/store/appStore";
import bgImage from "@/assets/favicon.png";
import { useSearchParams } from "react-router-dom";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { capitalize } from "@/lib/helper";
import * as XLSX from 'xlsx';

interface DocumentFilters {
  status: string[];
  documentType: string[];
  mimeType: string[];
  gdType: string[];
  state: string[];
  districtUnit: string[];
  policeStation: string[];
  entryForOfficer: string[];
  caseType: string[];
  caseCategory: any[];
  riskLevel: string[];
  officerName: string[];
  underSection: string[];
  createdDateFrom: string;
  createdDateTo: string;
  gdDateFrom: string;
  gdDateTo: string;
  courtName: string[];
  issuingOfficer: string[];
  noticeType: string[];
  bondAmountMin: string;
  bondAmountMax: string;
  fileSizeMin: string;
  fileSizeMax: string;
  hasGeneralDiary: boolean | null;
  hasCrpc129Notice: boolean | null;
  hasBNSL129Notice: boolean | null;
  hasCrpcBondOrder: boolean | null;
  hasOcrText: boolean | null;
  hasWeapons: boolean | null;
  suspect_name: string;
  suspect_father_name: string;
  act: string;
  section: string;
  questionerResponses: { [questionId: string]: boolean | null };
}

export default function Documents({ userId }: { userId: string | null }) {
  const [searchParams] = useSearchParams();
  const { _filterOptions } = useAppStore();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewingDocument, setViewingDocument] = useState<Document | null>(null);
  const [deleteDocument, setDeleteDocument] = useState<Document | null>(null);
  const [bondOrderDocument, setBondOrderDocument] = useState<Document | null>(null);
  const [bnsl129NoticeDocument, setBnsl129NoticeDocument] = useState<Document | null>(null);
  const [crpcBondOrderDocument, setCrpcBondOrderDocument] = useState<Document | null>(null);
  const [editGDDocument, setEditGDDocument] = useState<Document | null>(null);
  const userRole = getUserInfo()?.role;
  const canEditGD = ['ADMIN', 'SUPERADMIN', 'CO'].includes(userRole);
  const [totalPages, setTotalPages] = useState(0);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const selectedPoliceStation = searchParams.get("policeStation");
  const selectedCaseCategory = searchParams.get("caseCategory");
  const selectedRiskLevel = searchParams.get("riskLevel");


  const [filters, setFilters] = useState<DocumentFilters>({
    status: [],
    documentType: [],
    mimeType: [],
    gdType: [],
    state: [],
    districtUnit: [],
    policeStation: [],
    entryForOfficer: [],
    caseType: [],
    caseCategory: [],
    riskLevel: [],
    officerName: [],
    underSection: [],
    courtName: [],
    issuingOfficer: [],
    noticeType: [],
    bondAmountMin: "",
    bondAmountMax: "",
    fileSizeMin: "",
    fileSizeMax: "",
    hasGeneralDiary: null,
    hasCrpc129Notice: null,
    hasBNSL129Notice: null,
    hasCrpcBondOrder: null,
    hasOcrText: null,
    createdDateFrom: "",
    createdDateTo: "",
    gdDateFrom: "",
    gdDateTo: "",
    hasWeapons: null,
    suspect_name: "",
    suspect_father_name: "",
    act: "",
    section: "",
    questionerResponses: {},
  });

  const [filterOptions, setFilterOptions] = useState({
    status: [] as string[],
    documentType: [] as string[],
    mimeType: [] as string[],
    gdType: [] as string[],
    state: [] as string[],
    districtUnit: [] as string[],
    policeStation: [] as string[],
    entryForOfficer: [] as string[],
    caseType: [] as string[],
    caseCategory: [] as any[],
    riskLevel: [] as string[],
    officerName: [] as string[],
    underSection: [] as string[],
    courtName: [] as string[],
    issuingOfficer: [] as string[],
    noticeType: [] as string[],
  });

  const handleViewDocument = (document: Document) => {
    setViewingDocument(document);
  };

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
    fetchDocuments(itemsPerPage, value);
  };

  const confirmDelete = () => {
    if (deleteDocument) {
      setDocuments(documents.filter((doc) => doc.id !== deleteDocument.id));
      setDeleteDocument(null);
    }
  };

  const getCaseCategoryBadge = (caseCategory: string) => {
    const colorMap: Record<string, string> = {
      "संपत्ति विवाद": "bg-yellow-100 text-yellow-800",
      "महिला सम्बंधित विवाद": "bg-pink-100 text-pink-800",
      "रंजिश (गैंग/पूर्व मुकदमा सम्बंधित) विवाद": "bg-red-100 text-red-800",
      "चुनावी रंजिश विवाद": "bg-purple-100 text-purple-800",
      "रोडजाम / पुलिस के साथ अभद्रता": "bg-orange-100 text-orange-800",
      "मंदिर/मस्जिद/धर्मांतरण/त्योहार (होली, मोहर्रम इत्यादि)": "bg-blue-100 text-blue-800",
    };
    return <Badge className={`${colorMap[caseCategory] || "bg-gray-100 text-gray-800"} hover:${colorMap[caseCategory] || "bg-gray-100"}`}>{caseCategory}</Badge>;
  };

  const fetchFilterOptions = async () => {
    setFilterOptions((prev) => ({
      ...prev,
      gdType: _filterOptions.gdTypes,
      state: _filterOptions.states,
      districtUnit: _filterOptions.districtUnits,
      policeStation: _filterOptions.policeStations,
      officerName: _filterOptions.officerNames,
      riskLevel: _filterOptions.riskLevels,
      courtName: _filterOptions.courtNames,
      caseCategory: _filterOptions.caseCategories,
    }));
  };

  const fetchDocuments = async (limit = 10, page = 1) => {
    const queryParams: any = { limit, page };
    if (userId) queryParams["user_id"] = userId;

    Object.entries(filters).forEach(([key, value]) => {
      if (key === "questionerResponses" && typeof value === "object" && value !== null) {
        Object.entries(value).forEach(([questionId, response]) => {
          if (response !== null) {
            queryParams[`questioner_${questionId}`] = response;
          }
        });
      } else if (Array.isArray(value) && value.length > 0 && value[0] !== 'unknown') {
        queryParams[key] = value.join(",");
      } else if (typeof value === "string" && value.trim() && value.trim() !== 'unknown') {
        queryParams[key] = value;
      } else if (typeof value === "boolean") {
        queryParams[key] = value;
      }
    });

    if (searchTerm.trim()) {
      queryParams.search = searchTerm;
    }

    const jsonResponse: any = await apiService.get(`/documents`, queryParams, {});
    setDocuments(jsonResponse.data);
    setFilteredDocuments(jsonResponse.data);
    setTotalPages(jsonResponse.pagination.totalPages);
    setStartIndex(jsonResponse.pagination.limit * (jsonResponse.pagination.page - 1));
    setEndIndex(jsonResponse.pagination.limit * jsonResponse.pagination.page);
    setTotal(jsonResponse.pagination.total);
  };

  const handleFilterChange = (filterKey: keyof DocumentFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  };

  const handleMultiSelectFilter = async (filterKey: keyof DocumentFilters, value: string, replace: boolean = false) => {
    setFilters((prev) => {
      if (replace) {
        return {
          ...prev,
          [filterKey]: [value], // reset
        };
      }
      const currentValues = prev[filterKey] as string[];
      const newValues = currentValues.includes(value) ? currentValues.filter((v) => v !== value) : [...currentValues, value];
      return {
        ...prev,
        [filterKey]: newValues,
      };
    });
  };

  const clearAllFilters = () => {
    setFilters({
      status: [],
      documentType: [],
      mimeType: [],
      gdType: [],
      state: [],
      districtUnit: [],
      policeStation: [],
      entryForOfficer: [],
      caseType: [],
      caseCategory: [],
      riskLevel: [],
      officerName: [],
      underSection: [],
      createdDateFrom: "",
      createdDateTo: "",
      gdDateFrom: "",
      gdDateTo: "",
      courtName: [],
      issuingOfficer: [],
      noticeType: [],
      bondAmountMin: "",
      bondAmountMax: "",
      fileSizeMin: "",
      fileSizeMax: "",
      hasGeneralDiary: null,
      hasCrpc129Notice: null,
      hasBNSL129Notice: null,
      hasCrpcBondOrder: null,
      hasOcrText: null,
      hasWeapons: null,
      suspect_name: "",
      suspect_father_name: "",
      act: "",
      section: "",
      questionerResponses: {},
    });
    setSearchTerm("");
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchDocuments(itemsPerPage, 1);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([, value]) => {
      if (Array.isArray(value) && value.length > 0) count++;
      else if (typeof value === "string" && value.trim()) count++;
      else if (typeof value === "boolean") count++;
    });
    if (searchTerm.trim()) count++;
    return count;
  };

  const getBondAmount = (document: Document) => {
    if (document.crpc129Notice && document.crpc129Notice.boundDownOrder) {
      return document.crpc129Notice.boundDownOrder.bondAmount || "";
    }
    if (document.bNSL129Notice && document.bNSL129Notice.boundDownOrder) {
      return document.bNSL129Notice.boundDownOrder.bondAmount || "";
    }
    if (document.crpcBondOrder && document.crpcBondOrder.boundDownOrder) {
      return document.crpcBondOrder.boundDownOrder.bondAmount || "";
    }
    return "";
  };

  const getRiskLevel = (document: Document) => {
    const riskLevel = document.generalDiary?.riskLevel || "unknown";
    const colorMap: Record<string, string> = {
      low: "bg-orange-100 text-orange-800",
      medium: "bg-purple-100 text-purple-800",
      high: "bg-red-100 text-red-800",
      unknown: "bg-slate-100 text-slate-800",
    };
    const displayText = riskLevel === "unknown" ? "Unknown" : capitalize(riskLevel);

    return <Badge className={`${colorMap[riskLevel] || "bg-gray-100 text-gray-800"} hover:${colorMap[riskLevel] || "bg-gray-100"}`}>{displayText}</Badge>;
  };

  const init = async () => {
    if (selectedPoliceStation) {
      clearAllFilters();
      await handleMultiSelectFilter("policeStation", selectedPoliceStation, true);
    }
    if (selectedCaseCategory) {
      clearAllFilters();
      await handleMultiSelectFilter("caseCategory", selectedCaseCategory, true);
    }
    if(selectedRiskLevel) {
      clearAllFilters();
      await handleMultiSelectFilter("riskLevel", selectedRiskLevel, true);
    }
    setItemsPerPage(10);
    await fetchFilterOptions();
  };

  const questioner = () => {
    const selectedCategory: any = _filterOptions.caseCategories?.find((cat: any) => cat.title == filters.caseCategory[0]);
    return selectedCategory ? selectedCategory.questions.map((question: any) => ({
      ...question, 
      value: filters.questionerResponses[question.id] || null
    })) : []
  }

  const transformDocumentForExport = (doc: any) => {
    // Format suspects: "Name (Father: FatherName, Address: Address)"
    const suspectsFormatted = doc.generalDiary?.suspects?.map((s: any) =>
      `${s.name || ''}${s.fatherName ? ` (पिता: ${s.fatherName})` : ''}${s.address ? ` - ${s.address}` : ''}`
    ).join('; ') || '';

    // Format victims: "Name (Father: FatherName, Address: Address)"
    const victimsFormatted = doc.generalDiary?.victims?.map((v: any) =>
      `${v.name || ''}${v.fatherName ? ` (पिता: ${v.fatherName})` : ''}${v.address ? ` - ${v.address}` : ''}`
    ).join('; ') || '';

    // Format acts and sections: "Act - Section"
    const actsAndSectionsFormatted = doc.generalDiary?.actsAndSections?.map((a: any) =>
      `${a.act || ''}${a.section ? ` - ${a.section}` : ''}`
    ).join('; ') || '';

    // Format question responses: "Question: Yes/No"
    const questionResponseFormatted = doc.generalDiary?.questionResponse?.filter((qr: any) => qr.answer).map((qr: any) =>
      `${qr.question?.questionText || ''}: ${qr.answer ? 'हाँ' : 'नहीं'}`
    ).join('; ') || '';

    return {
      'GD No.': doc.generalDiary?.gdNo || '',
      'Case Category': doc.generalDiary?.categories[0]?.caseCategory?.title || '',
      'Police Station': doc.generalDiary?.policeStation?.name || '',
      'Officer Name': doc.generalDiary?.officerName || '',
      'District': doc.generalDiary?.districtUnit || '',
      'State': doc.generalDiary?.state || '',
      'Date': new Date(doc.createdAt).toLocaleDateString("en-IN"),
      'GD Date': doc.generalDiary?.gdDate ? new Date(doc.generalDiary.gdDate).toLocaleDateString("en-IN") : '',
      'Suspects': suspectsFormatted,
      'Victims': victimsFormatted,
      'Acts & Sections': actsAndSectionsFormatted,
      'Question Responses': questionResponseFormatted,
      'Has GD (107/126)': doc.generalDiary ? 'Yes' : 'No',
      'Has Interim Notice (111/130)': doc.crpc129Notice ? 'Yes' : 'No',
      'Has Interim Order (116_3/135_3)': doc.bNSL129Notice ? 'Yes' : 'No',
      'Has Bond Order (117/136)': doc.crpcBondOrder ? 'Yes' : 'No',
      'Bond Amount': getBondAmount(doc),
      'Risk Level': doc.generalDiary?.riskLevel || 'unknown',
      'File Name': doc.fileName || '',
      'File Size': doc.fileSize || '',
    };
  };

  const buildQueryParams = (includeFilters: boolean = false) => {
    const queryParams: any = { limit: 100000, page: 1 };    
    if (userId) queryParams["user_id"] = userId;
    if (includeFilters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (key === "questionerResponses" && typeof value === "object" && value !== null) {
          Object.entries(value).forEach(([questionId, response]) => {
            if (response !== null) {
              queryParams[`questioner_${questionId}`] = response;
            }
          });
        } else if (Array.isArray(value) && value.length > 0 && value[0] !== 'unknown') {
          queryParams[key] = value.join(",");
        } else if (typeof value === "string" && value.trim() && value.trim() !== 'unknown') {
          queryParams[key] = value;
        } else if (typeof value === "boolean") {
          queryParams[key] = value;
        }
      });
      if (searchTerm.trim()) {
        queryParams.search = searchTerm;
      }
    }
    return queryParams;
  };

  const generateExcelFile = (data: any[], filenameSuffix: string) => {
    const exportData = data.map(transformDocumentForExport);
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Documents');
    
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `documents_export_${filenameSuffix}_${timestamp}.xlsx`;
    
    XLSX.writeFile(wb, filename);
  };

  const exportToExcel = () => {
    generateExcelFile(filteredDocuments, 'page');
  };

  const exportFullDataToExcel = async () => {
    try {
      const queryParams = buildQueryParams(false);
      const jsonResponse: any = await apiService.get(`/documents`, queryParams, {});
      generateExcelFile(jsonResponse.data, 'full');
    } catch (error) {
      console.error('Error exporting full data:', error);
    }
  };

  const exportFilteredDataToExcel = async () => {
    try {
      const queryParams = buildQueryParams(true);
      const jsonResponse: any = await apiService.get(`/documents`, queryParams, {});
      generateExcelFile(jsonResponse.data, 'filtered');
    } catch (error) {
      console.error('Error exporting filtered data:', error);
    }
  };

  const buildGDEditData = (doc: Document) => {
    const gd = doc.generalDiary;
    const answersMap: Record<string, Record<string, string>> = {};
    if (gd.questionResponse) {
      for (const qr of gd.questionResponse as any[]) {
        const catId = qr.caseCategoryId?.toString();
        if (catId) {
          if (!answersMap[catId]) answersMap[catId] = {};
          answersMap[catId][qr.questionId] = qr.answer ? "Yes" : "No";
        }
      }
    }
    return {
      id: doc.id,
      file_name: doc.fileName,
      general_diary: {
        GD_No: gd.gdNo,
        GD_DateTime: gd.gdDateTime,
        GD_TYPE: gd.gdType,
        State: gd.state,
        District_Unit: gd.districtUnit,
        Police_Station: (gd.policeStation as any)?.name || gd.policeStation,
        Incident_Village: (gd as any).incidentVillage || "",
        Entry_For_Officer: gd.entryForOfficer,
        Information_Type: gd.informationType,
        Under_Section: gd.underSection,
        Officer_Name: gd.officerName,
        Case_Type: gd.caseType,
        Subject: gd.subject,
        Report_Printed_On: gd.reportPrintedOn,
        Report_Printed_By: { Name: gd.reportPrintedByName, Rank: gd.reportPrintedByRank },
        Signature: { Name: gd.signatureName, Rank: gd.signatureRank, ID_No: gd.signatureIdNo },
        riskLevel: gd.riskLevel,
        additionalDetails: (gd as any).additionalDetails || "",
        notes: (gd as any).notes || "",
        Details: {
          Incident_Description: gd.incidentDescription,
          Action_Taken: gd.actionTaken,
          First_Party: gd.suspects?.map((s: any) => ({ Name: s.name, Father_Name: s.fatherName, address: s.address })) || [],
          Second_Party: gd.victims?.map((v: any) => ({ Name: v.name, Father_Name: v.fatherName, address: v.address })) || [],
        },
        Acts_And_Sections: gd.actsAndSections?.map((a: any) => ({ Act: a.act, Section: a.section })) || [],
        weapons: gd.weapons || [],
      },
      _existingCategoryId: gd.categories?.[0]?.caseCategory?.id || null,
      _existingAnswers: answersMap,
    };
  };

  useEffect(() => {
    fetchDocuments();
  }, [filters, itemsPerPage]);

  useEffect(() => {
    init();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-9xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-black">
              Documents 
              {selectedPoliceStation ? ` (${selectedPoliceStation})` : ""}
              {selectedCaseCategory ? ` (${selectedCaseCategory})` : ""}
              {selectedRiskLevel ? ` (${selectedRiskLevel} risk)` : ""}
            </h1>
            <p className="text-gray-600 mt-1">Manage and view uploaded police documents</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-gray-600" />
                <div>
                  <div className="text-2xl font-bold text-black">{total}</div>
                  <p className="text-sm text-gray-600">Total Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold text-blue-600">{documents.filter((d) => d.generalDiary?.gdType === "FIR").length}</div>
                  <p className="text-sm text-gray-600">FIR Cases</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-green-600" />
                <div>
                  <div className="text-2xl font-bold text-green-600">{new Set(documents.map((d) => d.generalDiary?.policeStation)).size}</div>
                  <p className="text-sm text-gray-600">Police Stations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-purple-600" />
                <div>
                  <div className="text-2xl font-bold text-purple-600">{6}</div>
                  <p className="text-sm text-gray-600">Case Categories</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-black flex items-center justify-between">
              Documents List
              <div className="flex items-center gap-2">
              <Button 
                  onClick={exportFullDataToExcel} 
                  variant="outline"
                  size="sm"
                  className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Export Full Data
                </Button>
                {getActiveFiltersCount() > 0 && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                    {getActiveFiltersCount()} filters active
                  </Badge>
                )}
                <Button variant="outline" size="sm" onClick={clearAllFilters} className="text-sm bg-transparent">
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Search Bar */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input placeholder="Search documents..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 border-gray-300" />
              </div>
              <Dialog>
                <DialogTrigger className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white px-6 py-1 h-[35px] rounded-md flex">
                  Open Filters <Filter className="h-4 w-4 mt-[5px] ml-[10px]" />
                </DialogTrigger>
                <DialogContent className="bg-white flex flex-col w-[90%]" style={{ maxWidth: "1800px", maxHeight: "96vh" }}>
                  <DialogHeader>
                    <DialogTitle>Filters</DialogTitle>
                    <DialogDescription>{""}</DialogDescription>
                  </DialogHeader>
                  <div className="">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-1 mb-3 p-2 border border-2 bg-gray-50 rounded-lg">
                      <p className="text-xs col-span-5">General Details</p>
                      {/* GD Type Filter */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">GD Type</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-transparent">
                              <span>{filters.gdType.length > 0 ? `${filters.gdType.length} selected` : "All GD Types"}</span>
                              <Filter className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56">
                            <div className="space-y-2">
                              {filterOptions.gdType && filterOptions.gdType.map((type) => (
                                <div key={type} className="flex items-center space-x-2">
                                  <Checkbox checked={filters.gdType.includes(type)} onCheckedChange={() => handleMultiSelectFilter("gdType", type)} />
                                  <label className="text-sm">{type}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Case Category Filter */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Case Category</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-transparent">
                              <span>{filters.caseCategory.length > 0 ? `${filters.caseCategory.length} selected` : "All Case Category"}</span>
                              <Filter className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 max-h-96 overflow-y-auto">
                            <div className="space-y-2">
                              {filterOptions.caseCategory && filterOptions.caseCategory.map((category) => (
                                <div key={category.title} className="py-2">
                                  {/* Category checkbox */}
                                  <div className="flex items-center space-x-2">
                                    <Checkbox checked={filters.caseCategory.includes(category.title)} onCheckedChange={() => handleMultiSelectFilter("caseCategory", category.title)} />
                                    <label className="text-sm font-medium">{category.title}</label>
                                  </div>

                                  {/* Subcategory checkboxes */}
                                  {category.subcategories?.map((subcategory: string) => (
                                    <div key={`${category.title}::${subcategory}`} className="ml-4 flex items-center space-x-2">
                                      <Checkbox checked={filters.caseCategory.includes(subcategory)} onCheckedChange={() => handleMultiSelectFilter("caseCategory", subcategory)} />
                                      <label className="text-sm">{subcategory}</label>
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Risk Level Filter */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Risk Level</label>
                        <Select value={filters.riskLevel[0] || "unknown"} onValueChange={(value) => handleFilterChange("riskLevel", value ? [value] : [])}>
                          <SelectTrigger className="w-full font-semibold">
                            <SelectValue placeholder="All Risk Levels" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unknown">All Risk Levels</SelectItem>
                            {filterOptions.riskLevel && filterOptions.riskLevel.map((level) => (
                              <SelectItem key={level} value={level}>
                                {capitalize(level)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Date Range Filters */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Created Date From</label>
                        <Input type="date" max="9999-12-31" value={filters.createdDateFrom} onChange={(e) => handleFilterChange("createdDateFrom", e.target.value)} className="border-gray-300" />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Created Date To</label>
                        <Input type="date" max="9999-12-31" value={filters.createdDateTo} onChange={(e) => handleFilterChange("createdDateTo", e.target.value)} className="border-gray-300" />
                      </div>
                    </div>
                    {questioner() && questioner().length > 0 && (
                      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-1 mb-3 p-2 border border-2 bg-gray-50 rounded-lg`}>
                      <p className="text-xs col-span-4">Case Category Questioner</p>
                      {/* Questioner Filter */}
                      {questioner().map((question: any, index: number) => (
                        <div className="space-y-2" key={question.questionText + 'qnr' + index}>
                          <label className="text-sm font-medium text-gray-700">{question.questionText}</label>
                          <Select 
                            value={question.value === null ? "any" : question.value.toString()}
                            onValueChange={(value) => {
                              const newResponses = { ...filters.questionerResponses };
                              if (value === "any") {
                                delete newResponses[question.id];
                              } else {
                                newResponses[question.id] = value === "true";
                              }
                              handleFilterChange("questionerResponses", newResponses);
                            }}
                          >
                            <SelectTrigger className="w-full font-semibold">
                              <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">Any</SelectItem>
                              <SelectItem value="true">Yes</SelectItem>
                              <SelectItem value="false">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}                      
                    </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1 mb-3 p-2 border border-2 bg-gray-50 rounded-lg">
                      <p className="text-xs col-span-4">Location Filters</p>
                      {/* State Filter */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">State</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-transparent">
                              <span>{filters.state.length > 0 ? `${filters.state.length} selected` : "All States"}</span>
                              <Filter className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56">
                            <div className="space-y-2">
                              {filterOptions.state && filterOptions.state.map((state) => (
                                <div key={state} className="flex items-center space-x-2">
                                  <Checkbox checked={filters.state.includes(state)} onCheckedChange={() => handleMultiSelectFilter("state", state)} />
                                  <label className="text-sm">{state}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* District Filter */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">District</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-transparent">
                              <span>{filters.districtUnit.length > 0 ? `${filters.districtUnit.length} selected` : "All Districts"}</span>
                              <Filter className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56">
                            <div className="space-y-2">
                              {filterOptions.districtUnit && filterOptions.districtUnit.map((district) => (
                                <div key={district} className="flex items-center space-x-2">
                                  <Checkbox checked={filters.districtUnit.includes(district)} onCheckedChange={() => handleMultiSelectFilter("districtUnit", district)} />
                                  <label className="text-sm">{district}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Police Station Filter */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Police Station</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-transparent">
                              <span>{filters.policeStation.length > 0 ? `${filters.policeStation.length} selected` : "All Stations"}</span>
                              <Filter className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56">
                            <div className="space-y-2">
                              {filterOptions.policeStation && filterOptions.policeStation.map((station: any) => (
                                <div key={station.id} className="flex items-center space-x-2">
                                  <Checkbox checked={filters.policeStation.includes(station.name)} onCheckedChange={() => handleMultiSelectFilter("policeStation", station.name)} />
                                  <label className="text-sm">{station.name}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Court Name Filter */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Court Name</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-transparent">
                              <span>{filters.courtName.length > 0 ? `${filters.courtName.length} selected` : "All Courts"}</span>
                              <Filter className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56">
                            <div className="space-y-2">
                              {filterOptions.courtName && filterOptions.courtName.map((court) => (
                                <div key={court} className="flex items-center space-x-2">
                                  <Checkbox checked={filters.courtName.includes(court)} onCheckedChange={() => handleMultiSelectFilter("courtName", court)} />
                                  <label className="text-sm">{court}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-1 mb-3 p-2 border border-2 bg-gray-50 rounded-lg">
                      <p className="text-xs col-span-5">People & Legal References</p>
                      {/* Officer Name Filter */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Officer Name</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-transparent">
                              <span>{filters.officerName.length > 0 ? `${filters.officerName.length} selected` : "All Officers"}</span>
                              <Filter className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56">
                            <div className="space-y-2">
                              {filterOptions.officerName && filterOptions.officerName.map((officer) => (
                                <div key={officer} className="flex items-center space-x-2">
                                  <Checkbox checked={filters.officerName.includes(officer)} onCheckedChange={() => handleMultiSelectFilter("officerName", officer)} />
                                  <label className="text-sm">{officer}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Suspect Name</label>
                        <Input placeholder="Search suspect..." value={filters.suspect_name || ""} onChange={(e) => handleFilterChange("suspect_name", e.target.value)} className="pl-10 border-gray-300" />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Suspect Father Name</label>
                        <Input placeholder="Search suspect father..." value={filters.suspect_father_name || ""} onChange={(e) => handleFilterChange("suspect_father_name", e.target.value)} className="pl-10 border-gray-300" />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Act</label>
                        <Input placeholder="Search act..." value={filters.act || ""} onChange={(e) => handleFilterChange("act", e.target.value)} className="pl-10 border-gray-300" />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Section</label>
                        <Input placeholder="Search section..." value={filters.section || ""} onChange={(e) => handleFilterChange("section", e.target.value)} className="pl-10 border-gray-300" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-1 mb-3 p-2 border border-2 bg-gray-50 rounded-lg">
                      <p className="text-xs col-span-6">Bond / Interim Orders</p>
                      {/* Bond Amount Range */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Bond Amount Min</label>
                        <Input type="number" placeholder="Min amount" value={filters.bondAmountMin} onChange={(e) => handleFilterChange("bondAmountMin", e.target.value)} className="border-gray-300" />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Bond Amount Max</label>
                        <Input type="number" placeholder="Max amount" value={filters.bondAmountMax} onChange={(e) => handleFilterChange("bondAmountMax", e.target.value)} className="border-gray-300" />
                      </div>

                      {/* Boolean Filters */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">{"Has General Diary (107/126)"}</label>
                        <Select value={filters.hasGeneralDiary === null ? "any" : filters.hasGeneralDiary.toString()} onValueChange={(value) => handleFilterChange("hasGeneralDiary", value === "any" ? null : value === "true")}>
                          <SelectTrigger className="w-full font-semibold">
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="true">Yes</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">{"Has Interim Notice (111/130)"}</label>
                        <Select value={filters.hasCrpc129Notice === null ? "any" : filters.hasCrpc129Notice.toString()} onValueChange={(value) => handleFilterChange("hasCrpc129Notice", value === "any" ? null : value === "true")}>
                          <SelectTrigger className="w-full font-semibold">
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="true">Yes</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">{"Has Interim Order/Bond (116_3/135_3)"}</label>
                        <Select value={filters.hasBNSL129Notice === null ? "any" : filters.hasBNSL129Notice.toString()} onValueChange={(value) => handleFilterChange("hasBNSL129Notice", value === "any" ? null : value === "true")}>
                          <SelectTrigger className="w-full font-semibold">
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="true">Yes</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">{"Has Bond Order (117/136)"}</label>
                        <Select value={filters.hasCrpcBondOrder === null ? "any" : filters.hasCrpcBondOrder.toString()} onValueChange={(value) => handleFilterChange("hasCrpcBondOrder", value === "any" ? null : value === "true")}>
                          <SelectTrigger className="w-full font-semibold">
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="true">Yes</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1 mb-3 p-2 border border-2 bg-gray-50 rounded-lg">
                      <p className="text-xs col-span-4">Other</p>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Has Weapon</label>
                        <Select value={filters.hasWeapons === null ? "any" : filters.hasWeapons.toString()} onValueChange={(value) => handleFilterChange("hasWeapons", value === "any" ? null : value === "true")}>
                          <SelectTrigger className="w-full font-semibold">
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="true">Yes</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Case Type Filter */}
                    <div className="space-y-2 hidden">
                      <label className="text-sm font-medium text-gray-700">Case Type</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-between bg-transparent">
                            <span>{filters.caseType.length > 0 ? `${filters.caseType.length} selected` : "All Case Types"}</span>
                            <Filter className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56">
                          <div className="space-y-2">
                            {filterOptions.caseType && filterOptions.caseType.map((type) => (
                              <div key={type} className="flex items-center space-x-2">
                                <Checkbox checked={filters.caseType.includes(type)} onCheckedChange={() => handleMultiSelectFilter("caseType", type)} />
                                <label className="text-sm">{type}</label>
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Close</Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button onClick={applyFilters} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                Apply Filters
              </Button>
              <Button 
                onClick={exportToExcel} 
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <FileText className="h-4 w-4 mr-2" />
                Export Current Sheet
              </Button>
              <Button 
                onClick={exportFilteredDataToExcel} 
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <FileText className="h-4 w-4 mr-2" />
                Export Filtered Data
              </Button>
            </div>
            {/* Documents Table */}
            <div className="border border-gray-200 rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-black font-semibold w-[70px]">GD No.</TableHead>
                    <TableHead className="text-black font-semibold">Case Category</TableHead>
                    <TableHead className="text-black font-semibold">Police Station</TableHead>
                    <TableHead className="text-black font-semibold">Officer</TableHead>
                    <TableHead className="text-black font-semibold">Accused</TableHead>
                    <TableHead className="text-black font-semibold w-[80px]">Date</TableHead>
                    <TableHead className="text-black font-semibold text-center w-[60px]" title="CrPC 107 / BNSS 126">107/126</TableHead>
                    <TableHead className="text-black font-semibold text-center w-[60px]" title="CrPC 111 / BNSS 130">111/130</TableHead>
                    <TableHead className="text-black font-semibold text-center w-[70px]" title="CrPC 116(3) / BNSS 135(3)">116/135</TableHead>
                    <TableHead className="text-black font-semibold text-center w-[60px]" title="CrPC 117 / BNSS 136">117/136</TableHead>
                    <TableHead className="text-black font-semibold w-[90px]">Bond Amt.</TableHead>
                    <TableHead className="text-black font-semibold w-[80px]">Risk</TableHead>
                    <TableHead className="text-black font-semibold w-[50px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.map((document) => {
                    const accused = [
                      ...(document.generalDiary?.suspects ?? []),
                      ...(document.generalDiary?.victims ?? []),
                    ];
                    const accusedTooltip = accused.map((a: any) => a.name).join(", ");
                    const visibleAccused = accused.slice(0, 2);
                    const extraCount = accused.length - visibleAccused.length;
                    return (
                    <TableRow key={document.id} className="hover:bg-gray-50">
                      <TableCell className="text-gray-600 text-sm">{document.generalDiary?.gdNo}</TableCell>
                      <TableCell>{getCaseCategoryBadge(document.generalDiary?.categories[0]?.caseCategory?.title)}</TableCell>
                      <TableCell className="text-gray-600 text-sm">{document.generalDiary?.policeStation?.name}</TableCell>
                      <TableCell className="text-gray-600 text-sm">{document.generalDiary?.officerName}</TableCell>
                      <TableCell className="text-sm" title={accusedTooltip}>
                        {accused.length === 0 ? (
                          <span className="text-gray-400 italic text-xs">—</span>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            {visibleAccused.map((a: any) => (
                              <span key={a.id} className="text-gray-700 leading-tight truncate max-w-[160px] block">{a.name}</span>
                            ))}
                            {extraCount > 0 && (
                              <span className="text-xs text-blue-600 font-medium">+{extraCount} more</span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-600 text-sm">{new Date(document.createdAt).toLocaleDateString("en-IN")}</TableCell>
                      <TableCell className="text-center text-gray-600">{document.generalDiary ? "✅" : "📝"}</TableCell>
                      <TableCell
                        className="text-center text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => !document.crpc129Notice && setBondOrderDocument(document)}
                        title={document.crpc129Notice ? "Bond order already created" : "Click to create bond order"}
                      >
                        {document.crpc129Notice ? "✅" : "📝"}
                      </TableCell>
                      <TableCell
                        className="text-center text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => !document.bNSL129Notice && setBnsl129NoticeDocument(document)}
                        title={document.bNSL129Notice ? "BNSL 129 Notice already created" : "Click to create BNSL 129 Notice"}
                      >
                        {document.bNSL129Notice ? "✅" : "📝"}
                      </TableCell>
                      <TableCell
                        className="text-center text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => !document.crpcBondOrder && setCrpcBondOrderDocument(document)}
                        title={document.crpcBondOrder ? "CrPC Bond Order already created" : "Click to create CrPC Bond Order"}
                      >
                        {document.crpcBondOrder ? "✅" : "📝"}
                      </TableCell>
                      <TableCell className="text-gray-600 text-sm">{getBondAmount(document)}</TableCell>
                      <TableCell className="text-gray-600 text-sm">{getRiskLevel(document)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="hover:bg-gray-100 cursor-pointer" onClick={() => handleViewDocument(document)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canEditGD && document.generalDiary && (
                            <Button variant="ghost" size="sm" className="hover:bg-blue-100 cursor-pointer text-blue-600" onClick={() => setEditGDDocument(document)} title="Edit General Diary">
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {filteredDocuments.length === 0 && <div className="text-center py-8 text-gray-500">No documents found matching your search criteria.</div>}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, total)} of {total} users
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="border-gray-300">
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        // Show first page, last page, current page, and pages around current
                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                      })
                      .map((page, index, array) => (
                        <div key={page} className="flex items-center">
                          {index > 0 && array[index - 1] !== page - 1 && <span className="px-2 text-gray-400">...</span>}
                          <Button variant={currentPage === page ? "default" : "outline"} size="sm" onClick={() => handlePageChange(page)} className={currentPage === page ? "bg-black text-white" : "border-gray-300"}>
                            {page}
                          </Button>
                        </div>
                      ))}
                  </div>

                  <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="border-gray-300">
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Document View Popup */}
      <DocumentViewPopup open={!!viewingDocument} onOpenChange={() => setViewingDocument(null)} document={viewingDocument} />

      {/* Edit GD Popup */}
      {editGDDocument && (
        <GDFormPopup
          open={!!editGDDocument}
          onOpenChange={() => setEditGDDocument(null)}
          data={buildGDEditData(editGDDocument)}
          generalDiaryId={editGDDocument.generalDiary?.id}
          onSuccess={() => {
            fetchDocuments();
            setEditGDDocument(null);
          }}
        />
      )}

      {/* Bond Order Popup */}
      <BondOrderPopup
        open={!!bondOrderDocument}
        onOpenChange={() => setBondOrderDocument(null)}
        documentId={bondOrderDocument?.id || ""}
        policeStation={bondOrderDocument?.generalDiary?.policeStation || null}
        onSuccess={() => {
          fetchDocuments();
          setBondOrderDocument(null);
        }}
      />

      {/* BNSL 129 Notice Popup */}
      <BNSL129NoticePopup
        open={!!bnsl129NoticeDocument}
        onOpenChange={() => setBnsl129NoticeDocument(null)}
        documentId={bnsl129NoticeDocument?.id || ""}
        onSuccess={() => {
          fetchDocuments();
          setBnsl129NoticeDocument(null);
        }}
      />

      {/* CrPC Bond Order Popup */}
      <CrpcBondOrderPopup
        open={!!crpcBondOrderDocument}
        onOpenChange={() => setCrpcBondOrderDocument(null)}
        documentId={crpcBondOrderDocument?.id || ""}
        policeStation={crpcBondOrderDocument?.generalDiary?.policeStation || null}
        onSuccess={() => {
          fetchDocuments();
          setCrpcBondOrderDocument(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteDocument} onOpenChange={() => setDeleteDocument(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">Delete Document</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">Are you sure you want to delete "{deleteDocument?.fileName}"? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 text-white hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
